import { base64UrlToBytes, bytesToBase64Url } from '../api/base64url'
import { api } from '../api/client'
import type { Me } from '../api/generated/Me'
import type { NewPasskey } from '../api/generated/NewPasskey'
import type { Passkey } from '../api/generated/Passkey'
import type { PasskeyCreation } from '../api/generated/PasskeyCreation'
import type { PasskeyLogin } from '../api/generated/PasskeyLogin'
import type { PasskeyRequest } from '../api/generated/PasskeyRequest'

/** COSE identifier of ES256, the only algorithm the server accepts. */
const ES256 = -7
/** Validity of the server's challenges. */
const TIMEOUT_MS = 5 * 60_000
const OFFER_DONE_KEY = 'passkey.offer.done'

export function passkeysSupported(): boolean {
  return 'PublicKeyCredential' in window
}

/** Whether the browser suggests passkeys in the autofill of the email field. */
export async function conditionalLoginAvailable(): Promise<boolean> {
  return (
    passkeysSupported() &&
    'isConditionalMediationAvailable' in PublicKeyCredential &&
    (await PublicKeyCredential.isConditionalMediationAvailable())
  )
}

/** The user closed the browser's dialog, or the request was aborted: nothing to report. */
export function isCancelled(error: unknown): boolean {
  return (
    error instanceof DOMException &&
    (error.name === 'NotAllowedError' || error.name === 'AbortError')
  )
}

/** This authenticator already holds a passkey of the account (`excludeCredentials`). */
export function isAlreadyRegistered(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'InvalidStateError'
}

export function creationOptions(options: PasskeyCreation): PublicKeyCredentialCreationOptions {
  return {
    challenge: base64UrlToBytes(options.challenge),
    rp: { id: options.rp_id, name: 'Event' },
    user: {
      id: base64UrlToBytes(options.user_id),
      name: options.user_name,
      displayName: options.user_display_name,
    },
    pubKeyCredParams: [{ type: 'public-key', alg: ES256 }],
    excludeCredentials: options.exclude_credentials.map((id) => ({
      type: 'public-key',
      id: base64UrlToBytes(id),
    })),
    // Discoverable, so that logging in needs no email first
    authenticatorSelection: {
      residentKey: 'required',
      requireResidentKey: true,
      userVerification: 'required',
    },
    attestation: 'none',
    timeout: TIMEOUT_MS,
  }
}

export function requestOptions(options: PasskeyRequest): PublicKeyCredentialRequestOptions {
  return {
    challenge: base64UrlToBytes(options.challenge),
    rpId: options.rp_id,
    userVerification: 'required',
    timeout: TIMEOUT_MS,
  }
}

type AttestationResponse = Pick<
  AuthenticatorAttestationResponse,
  'clientDataJSON' | 'getAuthenticatorData' | 'getPublicKey' | 'getPublicKeyAlgorithm'
>

/** The API body for a created passkey; `null` without an ES256 public key. */
export function registrationBody(
  rawId: ArrayBuffer,
  response: AttestationResponse,
  name: string,
): NewPasskey | null {
  const publicKey = response.getPublicKey()
  if (!publicKey || response.getPublicKeyAlgorithm() !== ES256) return null
  return {
    name,
    credential_id: bytesToBase64Url(rawId),
    client_data_json: bytesToBase64Url(response.clientDataJSON),
    authenticator_data: bytesToBase64Url(response.getAuthenticatorData()),
    public_key: bytesToBase64Url(publicKey),
  }
}

type AssertionResponse = Pick<
  AuthenticatorAssertionResponse,
  'clientDataJSON' | 'authenticatorData' | 'signature' | 'userHandle'
>

export function assertionBody(rawId: ArrayBuffer, response: AssertionResponse): PasskeyLogin {
  return {
    credential_id: bytesToBase64Url(rawId),
    client_data_json: bytesToBase64Url(response.clientDataJSON),
    authenticator_data: bytesToBase64Url(response.authenticatorData),
    signature: bytesToBase64Url(response.signature),
    user_handle: response.userHandle ? bytesToBase64Url(response.userHandle) : null,
  }
}

/** Names a passkey after the device creating it, to tell the user's passkeys apart. */
export function deviceName(userAgent: string, maxTouchPoints: number): string {
  if (/iPhone|iPod/.test(userAgent)) return 'iPhone'
  // iPadOS reports itself as a Mac: only the touch screen tells them apart
  if (/iPad/.test(userAgent) || (/Macintosh/.test(userAgent) && maxTouchPoints > 1)) return 'iPad'
  if (/Android/.test(userAgent)) return 'Android'
  if (/Macintosh/.test(userAgent)) return 'Mac'
  if (/Windows/.test(userAgent)) return 'Windows'
  if (/CrOS/.test(userAgent)) return 'Chromebook'
  if (/Linux/.test(userAgent)) return 'Linux'
  return 'Appareil'
}

export async function createPasskey(): Promise<Passkey[]> {
  const options = await api.post<PasskeyCreation>('/passkeys/options', {})
  const credential = await navigator.credentials.create({ publicKey: creationOptions(options) })
  if (!(credential instanceof PublicKeyCredential)) throw new Error('No passkey created')
  const body = registrationBody(
    credential.rawId,
    credential.response as AuthenticatorAttestationResponse,
    deviceName(navigator.userAgent, navigator.maxTouchPoints),
  )
  if (!body) throw new Error('Passkey without an ES256 public key')
  const passkeys = await api.post<Passkey[]>('/passkeys', body)
  markOfferDone()
  return passkeys
}

export async function loginWithPasskey(
  mediation?: CredentialMediationRequirement,
  signal?: AbortSignal,
): Promise<Me> {
  const options = await api.post<PasskeyRequest>('/auth/passkey/options', {})
  const credential = await navigator.credentials.get({
    publicKey: requestOptions(options),
    mediation,
    signal,
  })
  if (!(credential instanceof PublicKeyCredential)) throw new Error('No passkey chosen')
  const me = await api.post<Me>(
    '/auth/passkey',
    assertionBody(credential.rawId, credential.response as AuthenticatorAssertionResponse),
  )
  markOfferDone()
  return me
}

/** Whether to offer creating a passkey: this device can, and has not created or declined one. */
export async function shouldOfferPasskey(): Promise<boolean> {
  if (!passkeysSupported()) return false
  try {
    if (localStorage.getItem(OFFER_DONE_KEY) !== null) return false
  } catch {
    return false
  }
  return PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
}

export function markOfferDone(): void {
  try {
    localStorage.setItem(OFFER_DONE_KEY, '1')
  } catch {}
}
